import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';

export default function PatientAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = () => {
    setLoading(true);
    api.get('/appointments').then(r => setAppointments(r.data.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    if (!window.confirm('Annuler ce rendez-vous ?')) return;
    try {
      await api.post('/appointments/' + id + '/cancel');
      toast.success('Rendez-vous annulé');
      load();
    } catch(e) { toast.error('Impossible d\'annuler'); }
  };

  const statusColor = (s) => ({
    pending:'bg-yellow-100 text-yellow-700',
    confirmed:'bg-green-100 text-green-700',
    cancelled:'bg-red-100 text-red-700',
    completed:'bg-blue-100 text-blue-700',
    no_show:'bg-gray-100 text-gray-500',
  }[s] || 'bg-gray-100 text-gray-700');

  const statusLabel = (s) => ({
    pending:'En attente', confirmed:'Confirmé',
    cancelled:'Annulé', completed:'Terminé', no_show:'Absent'
  }[s] || s);

  const typeLabel = (t) => ({
    in_person:'🏥 Cabinet', teleconsultation:'📹 Vidéo', home_visit:'🏠 Domicile'
  }[t] || t);

  const filtered = filter === 'all' ? appointments
    : appointments.filter(a => a.status === filter);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/patient/dashboard" className="text-green-600 font-medium">← Retour</Link>
        <h1 className="font-bold text-gray-800">Mes rendez-vous</h1>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Filtres */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {[['all','Tous'],['pending','En attente'],['confirmed','Confirmés'],['completed','Terminés'],['cancelled','Annulés']].map(([val,label]) => (
            <button key={val} onClick={() => setFilter(val)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filter===val ? 'bg-green-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-gray-400 py-8">Chargement...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-4xl mb-3">📅</p>
            <p className="text-gray-500 mb-4">Aucun rendez-vous</p>
            <Link to="/doctors" className="btn-primary px-6 py-2">Prendre un RDV</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(apt => (
              <div key={apt.id} className="card">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-bold text-gray-800">{apt.doctor?.user?.name}</p>
                    <p className="text-sm text-green-600">{apt.doctor?.specialty?.name_fr}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor(apt.status)}`}>
                    {statusLabel(apt.status)}
                  </span>
                </div>

                <div className="space-y-1 text-sm text-gray-600">
                  <p>📅 {new Date(apt.scheduled_at).toLocaleDateString('fr-FR', {weekday:'long', day:'numeric', month:'long', year:'numeric'})}</p>
                  <p>🕐 {new Date(apt.scheduled_at).toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}</p>
                  <p>{typeLabel(apt.type)}</p>
                  <p>💰 {parseInt(apt.amount).toLocaleString()} FCFA • {apt.payment_status === 'paid' ? '✅ Payé' : '⏳ En attente'}</p>
                  {apt.reference && <p className="text-xs text-gray-400">Réf: {apt.reference}</p>}
                </div>

                {['pending','confirmed'].includes(apt.status) && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
                    {apt.payment_status === 'pending' && apt.payment_method === 'mobile_money' && (
                      <Link to={'/patient/pay/' + apt.id}
                        className="flex-1 text-center bg-green-600 text-white py-2 rounded-xl text-sm font-medium">
                        💳 Payer maintenant
                      </Link>
                    )}
                    <button onClick={() => cancel(apt.id)}
                      className="flex-1 text-center border border-red-200 text-red-500 py-2 rounded-xl text-sm font-medium hover:bg-red-50">
                      Annuler
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
